import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import {
  ChevronLeft,
  Pencil,
  Check,
  X,
  Plus,
  Circle,
  Clock,
  AlertTriangle,
  CheckSquare,
  Calendar,
  LayoutGrid,
  BarChart3,
  Filter,
  StickyNote,
  Paperclip,
  Download,
  CheckCircle2,
  RotateCcw,
} from 'lucide-react';
import { apiGet, apiPatch, apiPost, apiDelete, apiDownloadUrl } from '../lib/api';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { PinButton } from '../components/PinButton';
import { LinkPicker } from '../components/LinkPicker';
import { sortPeopleWithSelf, displayName } from '../lib/people';

/* ── Types ─────────────────────────────────────────────── */

interface ScopeDetailTask {
  id: string;
  title: string;
  status: string;
  priority: string;
  due_date: string | null;
  assignee_id: string | null;
  assignee_name: string | null;
  created_at: string;
}

interface ScopeDetailMeetingSeries {
  id: string;
  name: string;
  description: string | null;
  cadence: string | null;
  instance_count: number;
  last_meeting_date: string | null;
}

interface ScopeDetailReferenceDoc {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
}

interface ScopeDetailNote {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
}

interface ScopeDetailAttachment {
  id: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  parent_type: string;
  parent_id: string;
  parent_name: string;
  created_at: string;
}

interface ScopeTaskStats {
  total: number;
  todo: number;
  in_progress: number;
  done: number;
  blocked: number;
}

interface ScopeDetails {
  id: string;
  name: string;
  description: string | null;
  color: string;
  status: string;
  created_at: string;
  meeting_series: ScopeDetailMeetingSeries[];
  tasks: ScopeDetailTask[];
  reference_docs: ScopeDetailReferenceDoc[];
  notes: ScopeDetailNote[];
  attachments: ScopeDetailAttachment[];
  task_stats: ScopeTaskStats;
}

interface Person {
  id: string;
  name: string;
  is_self?: boolean;
}

/* ── Constants ─────────────────────────────────────────── */

const STATUS_ICONS: Record<string, React.ReactNode> = {
  todo: <Circle size={14} className="text-text-muted" />,
  in_progress: <Clock size={14} className="text-accent" />,
  blocked: <AlertTriangle size={14} className="text-danger" />,
  done: <CheckSquare size={14} className="text-success" />,
  cancelled: <X size={14} className="text-text-muted" />,
};

const PRIORITY_COLORS: Record<string, string> = {
  urgent: 'text-danger',
  high: 'text-warning',
  medium: 'text-text-secondary',
  low: 'text-text-muted',
};

const PRIORITY_OPTIONS = ['urgent', 'high', 'medium', 'low'];

type TabId = 'overview' | 'tasks' | 'notes' | 'meetings' | 'docs' | 'files';

const TABS: { id: TabId; label: string; icon: React.ReactNode }[] = [
  { id: 'overview', label: 'Overview', icon: <BarChart3 size={14} /> },
  { id: 'tasks', label: 'Tasks', icon: <CheckSquare size={14} /> },
  { id: 'notes', label: 'Notes', icon: <StickyNote size={14} /> },
  { id: 'meetings', label: 'Meetings', icon: <Calendar size={14} /> },
  { id: 'docs', label: 'Views', icon: <LayoutGrid size={14} /> },
  { id: 'files', label: 'Files', icon: <Paperclip size={14} /> },
];

/* ── Main Component ───────────────────────────────────── */

export function ProjectDetailPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const location = useLocation();
  const toast = useToast();
  const navState = location.state as { from?: string; fromLabel?: string } | null;
  const [project, setProject] = useState<ScopeDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [taskFilter, setTaskFilter] = useState<string>('');

  // Inline editing
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const nameInputRef = useRef<HTMLInputElement>(null);

  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraft, setDescDraft] = useState('');

  // New task modal
  const [showNewTask, setShowNewTask] = useState(false);
  const [people, setPeople] = useState<Person[]>([]);

  const loadProject = useCallback(async () => {
    if (!projectId) return;
    try {
      const [details, ppl] = await Promise.all([
        apiGet<ScopeDetails>(`/scopes/${projectId}/details`),
        apiGet<Person[]>('/people'),
      ]);
      setProject(details);
      setPeople(ppl);
    } catch (err) {
      console.error('Failed to load project:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  /* ── Name Editing ──────────────────────────────────── */

  function startEditName() {
    if (!project) return;
    setNameDraft(project.name);
    setEditingName(true);
    setTimeout(() => nameInputRef.current?.focus(), 0);
  }

  async function saveName() {
    const trimmed = nameDraft.trim();
    if (trimmed && trimmed !== project?.name) {
      try {
        await apiPatch(`/scopes/${projectId}`, { name: trimmed });
        loadProject();
      } catch (err) {
        console.error('Failed to update project name:', err);
        toast.error('Failed to update project name');
      }
    }
    setEditingName(false);
  }

  /* ── Description Editing ───────────────────────────── */

  function startEditDesc() {
    if (!project) return;
    setDescDraft(project.description ?? '');
    setEditingDesc(true);
  }

  async function saveDesc() {
    const trimmed = descDraft.trim();
    if (trimmed !== (project?.description ?? '')) {
      try {
        await apiPatch(`/scopes/${projectId}`, { description: trimmed || null });
        loadProject();
      } catch (err) {
        console.error('Failed to update project description:', err);
        toast.error('Failed to update project description');
      }
    }
    setEditingDesc(false);
  }

  /* ── Complete / Reopen ────────────────────────────── */

  async function toggleProjectStatus() {
    if (!project) return;
    const newStatus = project.status === 'completed' ? 'active' : 'completed';
    try {
      await apiPatch(`/scopes/${projectId}`, { status: newStatus });
      toast.success(newStatus === 'completed' ? 'Project marked complete' : 'Project reopened');
      loadProject();
    } catch (err) {
      console.error('Failed to update project status:', err);
      toast.error('Failed to update project status');
    }
  }

  /* ── New Task ──────────────────────────────────────── */

  async function handleCreateTask(
    title: string,
    priority: string,
    assigneeId: string,
    dueDate: string
  ) {
    try {
      await apiPost('/tasks', {
        title,
        priority: priority || 'medium',
        assignee_id: assigneeId || null,
        due_date: dueDate || null,
        scope_ids: [projectId],
      });
      setShowNewTask(false);
      toast.success('Task created');
      loadProject();
    } catch (err) {
      console.error('Failed to create task:', err);
      toast.error('Failed to create task');
    }
  }

  /* ── Render ────────────────────────────────────────── */

  if (loading) {
    return (
      <div className="h-full overflow-auto p-3 sm:p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" />
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="h-full overflow-auto p-3 sm:p-6">
      <div className="max-w-4xl mx-auto">
        <Link
          to={navState?.from ?? '/projects'}
          className="flex items-center gap-1 text-sm text-text-muted hover:text-text-primary transition-colors mb-4"
        >
          <ChevronLeft size={16} /> {navState?.fromLabel ?? 'Back to Projects'}
        </Link>
        <div className="text-text-muted text-center py-16">Project not found.</div>
      </div>
      </div>
    );
  }

  const stats = project.task_stats;
  const completionPct = stats.total > 0 ? Math.round((stats.done / stats.total) * 100) : 0;

  const filteredTasks = taskFilter
    ? project.tasks.filter((t) => {
        if (taskFilter === 'open') return t.status !== 'done' && t.status !== 'cancelled';
        if (taskFilter === 'done') return t.status === 'done';
        return true;
      })
    : project.tasks;

  return (
    <div className="h-full overflow-auto p-3 sm:p-6">
    <div className="max-w-5xl mx-auto">
      {/* Back nav */}
      <div className="flex items-center gap-3 mb-4">
        <Link
          to={navState?.from ?? '/projects'}
          className="flex items-center gap-1 text-sm text-text-muted hover:text-text-primary transition-colors w-fit"
        >
          <ChevronLeft size={16} /> {navState?.fromLabel ?? 'Back to Projects'}
        </Link>
        {navState?.from && navState.from !== '/projects' && (
          <Link
            to="/projects"
            className="text-xs text-text-muted hover:text-text-secondary transition-colors"
          >
            All Projects
          </Link>
        )}
      </div>

      {/* Header */}
      <div className="mb-6">
        {/* Name */}
        <div className="flex items-center gap-3 mb-2">
          <div
            className="w-4 h-4 rounded-full flex-shrink-0"
            style={{ backgroundColor: project.color }}
          />
          {editingName ? (
            <div className="flex items-center gap-2 flex-1">
              <input
                ref={nameInputRef}
                type="text"
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveName();
                  if (e.key === 'Escape') setEditingName(false);
                }}
                onBlur={saveName}
                className="flex-1 text-2xl font-bold bg-bg-input border border-border rounded-md px-3 py-1 text-text-primary focus:outline-none focus:border-accent"
              />
              <button
                onClick={saveName}
                className="p-1 text-success hover:bg-bg-hover rounded"
              >
                <Check size={20} />
              </button>
              <button
                onClick={() => setEditingName(false)}
                className="p-1 text-text-muted hover:bg-bg-hover rounded"
              >
                <X size={20} />
              </button>
            </div>
          ) : (
            <h1
              className="text-2xl font-bold text-text-primary cursor-pointer hover:text-accent transition-colors group flex items-center gap-2"
              onClick={startEditName}
            >
              {project.name}
              <Pencil
                size={16}
                className="opacity-0 group-hover:opacity-50 transition-opacity"
              />
            </h1>
          )}
          <PinButton entityType="scope" entityId={projectId!} label={project.name} />
        </div>

        {/* Description */}
        {editingDesc ? (
          <div className="ml-7">
            <textarea
              value={descDraft}
              onChange={(e) => setDescDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setEditingDesc(false);
              }}
              rows={3}
              autoFocus
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent resize-none"
              placeholder="Add a description..."
            />
            <div className="flex gap-2 mt-1">
              <button
                onClick={saveDesc}
                className="px-3 py-1 text-xs bg-accent text-white rounded hover:bg-accent-hover"
              >
                Save
              </button>
              <button
                onClick={() => setEditingDesc(false)}
                className="px-3 py-1 text-xs text-text-muted hover:text-text-primary"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <p
            className="ml-7 text-sm text-text-secondary cursor-pointer hover:text-text-primary transition-colors group"
            onClick={startEditDesc}
          >
            {project.description || (
              <span className="text-text-muted italic">Click to add a description...</span>
            )}
            <Pencil
              size={12}
              className="inline ml-1 opacity-0 group-hover:opacity-50 transition-opacity"
            />
          </p>
        )}

        {/* Complete / Reopen button */}
        <div className="ml-7 mt-2">
          {project.status === 'completed' ? (
            <button
              onClick={toggleProjectStatus}
              className="flex items-center gap-1.5 text-xs text-accent hover:text-accent-hover transition-colors"
            >
              <RotateCcw size={12} /> Reopen Project
            </button>
          ) : (
            <button
              onClick={toggleProjectStatus}
              className="flex items-center gap-1.5 text-xs text-text-muted hover:text-success transition-colors"
            >
              <CheckCircle2 size={12} /> Mark Complete
            </button>
          )}
        </div>
      </div>

      {/* Quick Links */}
      <div className="mb-6 bg-bg-card border border-border rounded-lg p-4">
        <LinkPicker
          sourceType="scope"
          sourceId={project.id}
          relationship="related"
          allowedTargetTypes={['task', 'meeting_series', 'reference_doc', 'person']}
          label="Linked Items"
          placeholder="Link a task, meeting, doc, or person..."
          onLinkCreated={() => loadProject()}
          onLinkRemoved={() => loadProject()}
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border mb-6">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-accent text-accent'
                : 'border-transparent text-text-muted hover:text-text-secondary'
            }`}
          >
            {tab.icon}
            {tab.label}
            {tab.id === 'tasks' && (
              <span className="ml-1 text-xs opacity-70">{project.tasks.length}</span>
            )}
            {tab.id === 'notes' && (
              <span className="ml-1 text-xs opacity-70">{project.notes.length}</span>
            )}
            {tab.id === 'meetings' && (
              <span className="ml-1 text-xs opacity-70">
                {project.meeting_series.length}
              </span>
            )}
            {tab.id === 'docs' && (
              <span className="ml-1 text-xs opacity-70">
                {project.reference_docs.length}
              </span>
            )}
            {tab.id === 'files' && (
              <span className="ml-1 text-xs opacity-70">{project.attachments.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <OverviewTab stats={stats} completionPct={completionPct} />
      )}

      {activeTab === 'tasks' && (
        <TasksTab
          tasks={filteredTasks}
          taskFilter={taskFilter}
          onFilterChange={setTaskFilter}
          onNewTask={() => setShowNewTask(true)}
          projectId={project.id}
          projectName={project.name}
        />
      )}

      {activeTab === 'notes' && (
        <NotesTab
          notes={project.notes}
          projectId={project.id}
          projectName={project.name}
          onNoteCreated={loadProject}
        />
      )}

      {activeTab === 'meetings' && (
        <MeetingsTab meetingSeries={project.meeting_series} />
      )}

      {activeTab === 'docs' && <DocsTab docs={project.reference_docs} />}

      {activeTab === 'files' && (
        <FilesTab attachments={project.attachments} projectId={projectId!} onReload={loadProject} />
      )}

      {/* New Task Modal */}
      {showNewTask && (
        <CreateTaskModal
          people={people}
          onSave={handleCreateTask}
          onClose={() => setShowNewTask(false)}
        />
      )}
    </div>
    </div>
  );
}

/* ── Overview Tab ──────────────────────────────────────── */

function OverviewTab({
  stats,
  completionPct,
}: {
  stats: ScopeTaskStats;
  completionPct: number;
}) {
  return (
    <div>
      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        <StatCard label="Total" value={stats.total} color="text-text-primary" />
        <StatCard label="To Do" value={stats.todo} color="text-text-muted" />
        <StatCard label="In Progress" value={stats.in_progress} color="text-accent" />
        <StatCard label="Done" value={stats.done} color="text-success" />
        <StatCard label="Blocked" value={stats.blocked} color="text-danger" />
      </div>

      {/* Progress Bar */}
      <div className="bg-bg-card border border-border rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-text-secondary">Completion</span>
          <span className="text-sm font-semibold text-text-primary">{completionPct}%</span>
        </div>
        <div className="w-full h-2.5 bg-bg-input rounded-full overflow-hidden">
          <div
            className="h-full bg-success rounded-full transition-all duration-500"
            style={{ width: `${completionPct}%` }}
          />
        </div>
        {stats.total > 0 && (
          <p className="text-xs text-text-muted mt-2">
            {stats.done} of {stats.total} task{stats.total !== 1 ? 's' : ''} completed
          </p>
        )}
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) {
  return (
    <div className="bg-bg-card border border-border rounded-lg p-3 text-center">
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
      <div className="text-xs text-text-muted mt-0.5">{label}</div>
    </div>
  );
}

/* ── Tasks Tab ─────────────────────────────────────────── */

function TasksTab({
  tasks,
  taskFilter,
  onFilterChange,
  onNewTask,
  projectId,
  projectName,
}: {
  tasks: ScopeDetailTask[];
  taskFilter: string;
  onFilterChange: (f: string) => void;
  onNewTask: () => void;
  projectId: string;
  projectName: string;
}) {
  return (
    <div>
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Filter size={14} className="text-text-muted" />
          <select
            value={taskFilter}
            onChange={(e) => onFilterChange(e.target.value)}
            className="px-2 py-1 bg-bg-input border border-border rounded text-sm text-text-primary"
          >
            <option value="">All</option>
            <option value="open">Open</option>
            <option value="done">Done</option>
          </select>
          {taskFilter && (
            <button
              onClick={() => onFilterChange('')}
              className="text-xs text-text-muted hover:text-text-primary"
            >
              Clear
            </button>
          )}
        </div>
        <button
          onClick={onNewTask}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent text-white text-sm font-medium hover:bg-accent-hover transition-colors"
        >
          <Plus size={16} /> New Task
        </button>
      </div>

      {tasks.length === 0 ? (
        <div className="text-center py-12 text-text-muted">
          <CheckSquare size={32} className="mx-auto mb-3 opacity-50" />
          <p className="text-sm">No tasks in this project yet.</p>
        </div>
      ) : (
        <div className="space-y-1">
          {tasks.map((task) => (
            <Link
              key={task.id}
              to={`/tasks/${task.id}`}
              state={{ from: `/projects/${projectId}`, fromLabel: projectName }}
              className="flex items-center gap-3 px-4 py-3 bg-bg-card border border-border rounded-lg hover:border-border-hover transition-colors group"
            >
              <span className="flex-shrink-0">
                {STATUS_ICONS[task.status] ?? <Circle size={14} />}
              </span>
              <div className="flex-1 min-w-0">
                <span
                  className={`text-sm ${
                    task.status === 'done'
                      ? 'line-through text-text-muted'
                      : 'text-text-primary'
                  }`}
                >
                  {task.title}
                </span>
                {task.assignee_name && (
                  <span className="text-xs text-text-muted ml-2">
                    @{task.assignee_name}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                {task.due_date && (
                  <span
                    className={`text-xs ${
                      isOverdue(task.due_date) && task.status !== 'done'
                        ? 'text-danger'
                        : 'text-text-muted'
                    }`}
                  >
                    {formatShortDate(task.due_date)}
                  </span>
                )}
                <span
                  className={`text-xs capitalize ${
                    PRIORITY_COLORS[task.priority] ?? 'text-text-muted'
                  }`}
                >
                  {task.priority}
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Meetings Tab ──────────────────────────────────────── */

function MeetingsTab({
  meetingSeries,
}: {
  meetingSeries: ScopeDetailMeetingSeries[];
}) {
  if (meetingSeries.length === 0) {
    return (
      <div className="text-center py-12 text-text-muted">
        <Calendar size={32} className="mx-auto mb-3 opacity-50" />
        <p className="text-sm">No meeting series linked to this project.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {meetingSeries.map((ms) => (
        <Link
          key={ms.id}
          to={`/meetings/${ms.id}`}
          className="flex items-center justify-between px-4 py-3 bg-bg-card border border-border rounded-lg hover:border-border-hover transition-colors"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-accent flex-shrink-0" />
              <span className="text-sm font-medium text-text-primary truncate">
                {ms.name}
              </span>
              {ms.cadence && (
                <span className="text-xs text-text-muted capitalize">{ms.cadence}</span>
              )}
            </div>
            {ms.description && (
              <p className="text-xs text-text-muted mt-1 ml-6 truncate">
                {ms.description}
              </p>
            )}
          </div>
          <div className="flex items-center gap-4 ml-4 text-xs text-text-muted">
            <span>
              {ms.instance_count} instance{ms.instance_count !== 1 ? 's' : ''}
            </span>
            {ms.last_meeting_date && (
              <span>Last: {formatShortDate(ms.last_meeting_date)}</span>
            )}
          </div>
        </Link>
      ))}
    </div>
  );
}

/* ── Docs Tab ──────────────────────────────────────────── */

function DocsTab({ docs }: { docs: ScopeDetailReferenceDoc[] }) {
  if (docs.length === 0) {
    return (
      <div className="text-center py-12 text-text-muted">
        <LayoutGrid size={32} className="mx-auto mb-3 opacity-50" />
        <p className="text-sm">No views linked to this project.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {docs.map((doc) => (
        <Link
          key={doc.id}
          to={`/views/${doc.id}`}
          className="flex items-center justify-between px-4 py-3 bg-bg-card border border-border rounded-lg hover:border-border-hover transition-colors"
        >
          <div className="flex items-center gap-2">
            <LayoutGrid size={16} className="text-accent flex-shrink-0" />
            <span className="text-sm font-medium text-text-primary">{doc.title}</span>
          </div>
          <span className="text-xs text-text-muted">
            Updated {formatDate(doc.updated_at)}
          </span>
        </Link>
      ))}
    </div>
  );
}

/* ── Notes Tab ─────────────────────────────────────────── */

function NotesTab({
  notes,
  projectId,
  projectName,
  onNoteCreated,
}: {
  notes: ScopeDetailNote[];
  projectId: string;
  projectName: string;
  onNoteCreated: () => void;
}) {
  const navigate = useNavigate();
  const toast = useToast();

  async function handleNewNote() {
    try {
      const note = await apiPost<{ id: string }>('/notes', {
        title: 'Untitled Note',
        scope_ids: [projectId],
      });
      toast.success('Note created');
      onNoteCreated();
      navigate(`/notes/${note.id}`, { state: { from: `/projects/${projectId}`, fromLabel: projectName } });
    } catch (err) {
      console.error('Failed to create note:', err);
      toast.error('Failed to create note');
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div />
        <button
          onClick={handleNewNote}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent text-white text-sm font-medium hover:bg-accent-hover transition-colors"
        >
          <Plus size={16} /> New Note
        </button>
      </div>
      {notes.length === 0 ? (
        <div className="text-center py-12 text-text-muted">
          <StickyNote size={32} className="mx-auto mb-3 opacity-50" />
          <p className="text-sm">No notes in this project yet.</p>
        </div>
      ) : (
        <div className="space-y-1">
          {notes.map((note) => (
            <Link
              key={note.id}
              to={`/notes/${note.id}`}
              state={{ from: `/projects/${projectId}`, fromLabel: projectName }}
              className="flex items-center justify-between px-4 py-3 bg-bg-card border border-border rounded-lg hover:border-border-hover transition-colors"
            >
              <div className="flex items-center gap-2">
                <StickyNote size={16} className="text-[#14b8a6] flex-shrink-0" />
                <span className="text-sm font-medium text-text-primary">{note.title}</span>
              </div>
              <span className="text-xs text-text-muted">
                Updated {formatDate(note.updated_at)}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Files Tab ─────────────────────────────────────────── */

function FilesTab({ attachments, projectId, onReload }: {
  attachments: ScopeDetailAttachment[];
  projectId: string;
  onReload: () => void;
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('scope_id', projectId);
      const { getIdToken } = await import('../lib/firebase');
      const token = await getIdToken();
      const res = await fetch('/api/attachments', {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      toast.success('File uploaded');
      onReload();
    } catch {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleDownload(att: ScopeDetailAttachment) {
    try {
      const blobUrl = await apiDownloadUrl(`/attachments/${att.id}/download`);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = att.filename;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error('Failed to download');
    }
  }

  async function handleDelete(att: ScopeDetailAttachment) {
    if (!await confirm({ title: `Delete "${att.filename}"?`, message: 'This will permanently remove the file.', variant: 'danger', confirmLabel: 'Delete' })) return;
    try {
      await apiDelete(`/attachments/${att.id}`);
      toast.success('File deleted');
      onReload();
    } catch {
      toast.error('Failed to delete file');
    }
  }

  const PARENT_LINKS: Record<string, (id: string) => string> = {
    task: (id) => `/tasks/${id}`,
    note: (id) => `/notes/${id}`,
    meeting: (id) => `/meetings/instance/${id}`,
    project: () => '#',
  };

  return (
    <div>
      <div className="mb-4">
        <input ref={fileInputRef} type="file" className="hidden" onChange={handleUpload} />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-accent text-white rounded-md hover:bg-accent-hover transition-colors disabled:opacity-50"
        >
          <Plus size={14} /> {uploading ? 'Uploading...' : 'Upload File'}
        </button>
      </div>

      {attachments.length === 0 ? (
        <div className="text-center py-12 text-text-muted">
          <Paperclip size={32} className="mx-auto mb-3 opacity-50" />
          <p className="text-sm">No files in this project yet.</p>
          <p className="text-xs mt-1">Upload files directly or attach them to tasks, notes, or meetings in this project.</p>
        </div>
      ) : (
        <div className="space-y-1">
          {attachments.map((att) => {
            const parentLink = PARENT_LINKS[att.parent_type]?.(att.parent_id);
            return (
              <div
                key={att.id}
                className="flex items-center gap-3 px-4 py-3 bg-bg-card border border-border rounded-lg group"
              >
                <Paperclip size={16} className="text-text-muted flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-text-primary truncate block">
                    {att.filename}
                  </span>
                  <div className="flex items-center gap-2 mt-0.5 text-xs text-text-muted">
                    {parentLink && parentLink !== '#' ? (
                      <Link to={parentLink} className="capitalize hover:text-accent transition-colors">
                        {att.parent_type} · {att.parent_name}
                      </Link>
                    ) : (
                      <span>{att.parent_type === 'project' ? 'Direct upload' : `${att.parent_type} · ${att.parent_name}`}</span>
                    )}
                    <span>·</span>
                    <span>{formatFileSize(att.size_bytes)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
                  <button
                    onClick={() => handleDownload(att)}
                    className="p-1.5 text-text-muted hover:text-text-primary hover:bg-bg-hover rounded transition-colors"
                    title="Download"
                  >
                    <Download size={14} />
                  </button>
                  {att.parent_type === 'project' && (
                    <button
                      onClick={() => handleDelete(att)}
                      className="p-1.5 text-text-muted hover:text-danger hover:bg-bg-hover rounded transition-colors"
                      title="Delete"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── Create Task Modal ─────────────────────────────────── */

function CreateTaskModal({
  people,
  onSave,
  onClose,
}: {
  people: Person[];
  onSave: (title: string, priority: string, assigneeId: string, dueDate: string) => void;
  onClose: () => void;
}) {
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState('medium');
  const [assigneeId, setAssigneeId] = useState('');
  const [dueDate, setDueDate] = useState('');

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="bg-bg-surface border border-border rounded-xl p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-text-primary mb-4">
          New Task in Project
        </h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-text-secondary mb-1">Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
              placeholder="What needs to be done?"
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent text-sm"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-text-secondary mb-1">Priority</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary text-sm"
              >
                {PRIORITY_OPTIONS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-text-secondary mb-1">Due Date</label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-1">Assignee</label>
            <select
              value={assigneeId}
              onChange={(e) => setAssigneeId(e.target.value)}
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary text-sm"
            >
              <option value="">Unassigned</option>
              {sortPeopleWithSelf(people).map((p) => (
                <option key={p.id} value={p.id}>
                  {displayName(p)}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary"
          >
            Cancel
          </button>
          <button
            onClick={() =>
              title.trim() && onSave(title.trim(), priority, assigneeId, dueDate)
            }
            disabled={!title.trim()}
            className="px-4 py-2 text-sm bg-accent text-white rounded-md hover:bg-accent-hover disabled:opacity-50"
          >
            Create
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Utility Functions ─────────────────────────────────── */

function formatShortDate(d: string): string {
  return new Date(d + (d.includes('T') ? '' : 'T00:00:00')).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function isOverdue(d: string): boolean {
  return new Date(d + 'T23:59:59') < new Date();
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
